// src/pages/Analytics.jsx
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "../Dashboard.css"

import {
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    AreaChart,
    Area,
} from "recharts";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollTop";
import PhoneLoader from "../components/loader/PhoneLoader";
import { useAuth } from "../provider/authProvider";

import { ImCalendar } from "react-icons/im";

import moment from "moment";
import "moment/locale/es";
import "moment-timezone";
moment.locale("es");

import Flatpickr from "flatpickr";
import Select from "react-select";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import "flatpickr/dist/themes/material_green.css";


import UserDetailModal from "../components/analytics/UserDetailModal";

const formatMoney = (v) =>
    new Intl.NumberFormat("es-PE", {
        style: "currency",
        currency: "PEN",
        minimumFractionDigits: 2,
    }).format(Number(v || 0));

const formatNumber = (v) =>
    new Intl.NumberFormat("es-PE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(v || 0));

const formatPercent = (v) =>
    `${new Intl.NumberFormat("es-PE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(Number(v || 0))}%`;

const TZ = "America/Lima";
const PRIMARY_COLOR = "#3153FF"; // azul similar al diseño de referencia

const getBrandFromPhone = (phone) =>
    (phone || "").toString().trim().split(" ")[0] || "";


// Estilos modernos react-select
const selectStyles = {
    control: (base) => ({
        ...base,
        background: "#fff",
        borderColor: "#e2e8f0",
        minHeight: 36,
        height: 36,
        boxShadow: "none",
        borderRadius: 8,
        paddingLeft: 2,
        "&:hover": { borderColor: "#cbd5e1" },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: 8,
        marginTop: 4,
        boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
        zIndex: 9999,
    }),
    option: (base, { isFocused }) => ({
        ...base,
        background: isFocused ? "#F3F4F6" : "white",
        color: "#111827",
        cursor: "pointer",
        fontSize: 14,
        padding: "8px 12px",
    }),
    singleValue: (base) => ({
        ...base,
        color: "#111827",
        fontSize: 14,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: 36,
    }),
};

export default function Analytics(props) {
    const { user } = useAuth();

    const today = new Date();
    const todayStr = moment(today).tz(TZ).format("YYYY-MM-DD");

    // Primer día del mes actual
    const firstDayMonth = moment(today).startOf("month").tz(TZ).format("YYYY-MM-DD");

    // Estado inicial correcto
    const [startDate, setStartDate] = useState(firstDayMonth);
    const [endDate, setEndDate] = useState(todayStr);



    const [sedeID, setSedeID] = useState("0");

    const [sedes, setSedes] = useState([]);
    const [summary, setSummary] = useState(null);
    const [seriesDay, setSeriesDay] = useState([]);
    const [seriesSede, setSeriesSede] = useState([]);
    const [seriesUser, setSeriesUser] = useState([]);
    const [seriesSegment, setSeriesSegment] = useState([]);

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // NUEVOS filtros globales
    const [filterAsesor, setFilterAsesor] = useState("0");
    const [filterSegment, setFilterSegment] = useState("ALL");
    const [filterBrand, setFilterBrand] = useState("ALL");

    const urlB = process.env.REACT_APP_API;

    // ======== Calendar (mismo estilo que Register.jsx) ========
    const calendarInputRef = useRef(null);
    const [showText, setShowText] = useState(() => {
        const ini = moment(firstDayMonth).format("DD MMMM");
        const fin = moment(todayStr).format("DD MMMM");
        return `Mostrando del "${ini}" al "${fin}"`;
    });


    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!calendarInputRef.current) return;

            Flatpickr(calendarInputRef.current, {
                locale: Spanish,
                mode: "range",
                dateFormat: "Y-m-d",
                defaultDate: [firstDayMonth, todayStr],
                closeOnSelect: false,
                disable: [
                    function(date) {
                        // mismo criterio que Register.jsx
                        const minDate = new Date("2025-07-11");
                        const maxDate = new Date();
                        return date < minDate || date > maxDate;
                    },
                ],
                onChange: (selectedDates, _dateStr, instance) => {
                    if (selectedDates.length < 2) {
                        // obliga a seleccionar el rango completo
                        setTimeout(() => instance.open(), 0);
                    }

                    if (selectedDates.length === 2) {
                        const [s, e] = selectedDates;

                        const f1 = moment(s).tz(TZ).format("YYYY-MM-DD");
                        const f2 = moment(e).tz(TZ).format("YYYY-MM-DD");

                        setStartDate(f1);
                        setEndDate(f2);

                        const formatDate = (date) =>
                            date.toLocaleDateString("es-ES", {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                            });

                        const sameDay =
                            s.toDateString() === e.toDateString();
                        const text = sameDay
                            ? `Mostrando "${formatDate(s)}"`
                            : `Mostrando del "${formatDate(
                                s
                            )}" al "${formatDate(e)}"`;

                        setShowText(text);
                        instance.close();
                    }
                },
            });
        }, 150);

        return () => clearTimeout(timeout);
    }, []);

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    });

    // ========== Cargar sedes ==========
    useEffect(() => {
        const fetchSedes = async () => {
            try {
                // uso mismo endpoint de sedes que en Register.jsx
                const { data } = await axios.get(
                    `${urlB}/register/get-sedes-filter`,
                    { headers: getAuthHeaders() }
                );
                const sedesArr = data?.data || [];
                setSedes(
                    sedesArr.map((s) => ({
                        id: s.idNumber,
                        name: s.sede,
                    }))
                );
            } catch (e) {
                console.error("Error cargando sedes:", e);
            }
        };
        fetchSedes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ========== Cargar analíticas ==========
    const loadData = async () => {
        setLoading(true);
        setErrorMsg("");

        const params = { startDate, endDate };
        if (sedeID && sedeID !== "0") params.sedeID = sedeID;

        const config = {
            params,
            headers: getAuthHeaders(),
        };

        try {
            const [resSummary, resDay, resSede, resUser, resSegment] =
                await Promise.all([
                    axios.get(
                        `${urlB}/api/analytics/kpis/summary`,
                        config
                    ),
                    axios.get(
                        `${urlB}/api/analytics/series/by-day`,
                        config
                    ),
                    axios.get(
                        `${urlB}/api/analytics/series/by-sede`,
                        config
                    ),
                    axios.get(
                        `${urlB}/api/analytics/series/by-user`,
                        config
                    ),
                    axios.get(
                        `${urlB}/api/analytics/series/by-segment`,
                        config
                    ),
                ]);

            setSummary(resSummary.data.data || null);
            setSeriesDay(resDay.data.data || []);
            setSeriesSede(resSede.data.data || []);
            setSeriesUser(resUser.data.data || []);
            setSeriesSegment(resSegment.data.data || []);
        } catch (e) {
            console.error("Error cargando analíticas:", e);
            const msgApi =
                e?.response?.data?.error ||
                e?.response?.data?.msg ||
                "Ocurrió un error al cargar las estadísticas.";
            setErrorMsg(msgApi);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, sedeID]);

    const kpis = summary?.kpis || {};
    const advanced = summary?.advanced || {};

    // ======== KPIs calculados en front ========
    const totalRegistros = Number(kpis.total_registros || 0);
    const ingresosGlobales = Number(kpis.ingresos_globales || 0);
    const montoOnline = Number(kpis.monto_online || 0);

    const ticketPromedio =
        totalRegistros > 0 ? ingresosGlobales / totalRegistros : 0;

    const porcentajeOnline =
        ingresosGlobales > 0 ? (montoOnline / ingresosGlobales) * 100 : 0;
    const porcentajeEfectivo = 100 - porcentajeOnline;

    // ======== Datos avanzados (segmentos / marcas / horas / contado / FM) ========
    const payjoyBySede = advanced.topPayjoyBySede || [];
    const payjoyByUser = advanced.topPayjoyByUser || [];
    const movilesBySede = advanced.topMovilespayBySede || [];
    const movilesByUser = advanced.topMovilespayByUser || [];
    const efectivaBySede = advanced.topEfectivaBySede || [];
    const efectivaByUser = advanced.topEfectivaByUser || [];
    const contadoBySede = advanced.topContadoBySede || [];
    const contadoByUser = advanced.topContadoByUser || [];

    const fmByUser = advanced.fmByUser || [];
    const fmBySede = advanced.fmBySede || [];

    const rankingPhones = advanced.rankingPhones || [];
    const rankingBrands = advanced.rankingBrands || [];
    const salesByHour = advanced.salesByHour || [];
    const peakHour = advanced.peakHour || null;

    const cashSalesList = advanced.cashSalesList || [];

    // =======================
    // Opciones para filtros
    // =======================

    // Asesores únicos (de seriesUser, fmByUser, cashSalesList)
    const advisorOptions = (() => {
        const map = new Map();
        const addUser = (r) => {
            const id = r.userID || r.fk_user_id;
            if (!id) return;
            const key = String(id);
            if (map.has(key)) return;
            const fullName =
                r.fullName ||
                `${r.name || ""} ${r.paternalSur || ""}`.trim() ||
                `Usuario ${key}`;
            map.set(key, fullName);
        };

        seriesUser.forEach(addUser);
        fmByUser.forEach(addUser);
        cashSalesList.forEach(addUser);

        return Array.from(map.entries()).map(([userID, fullName]) => ({
            userID,
            fullName,
        }));
    })();

    // Marcas únicas (primera palabra)
    const brandOptions = (() => {
        const set = new Set();
        rankingBrands.forEach((r) => {
            if (r.brand) set.add(r.brand);
        });
        return Array.from(set);
    })();

    // =======================
    // Aplicar filtros
    // =======================

    // Filtro por asesor
    const filteredSeriesUser =
        filterAsesor === "0"
            ? seriesUser
            : seriesUser.filter(
                (u) => String(u.userID) === String(filterAsesor)
            );

    const filteredFmByUser =
        filterAsesor === "0"
            ? fmByUser
            : fmByUser.filter(
                (r) => String(r.userID) === String(filterAsesor)
            );

    const filteredContadoList =
        filterAsesor === "0"
            ? cashSalesList
            : cashSalesList.filter(
                (r) => String(r.userID) === String(filterAsesor)
            );

    // Filtro por segmento (tabla de segmentos)
    const filteredSeriesSegment =
        filterSegment === "ALL"
            ? seriesSegment
            : seriesSegment.filter(
                (s) => s.segmento === filterSegment
            );

    // Filtro por segmento para ranking de sedes por segmento
    const filteredPayjoyBySede =
        filterSegment === "ALL" || filterSegment === "PayJoy"
            ? payjoyBySede
            : [];
    const filteredMovilesBySede =
        filterSegment === "ALL" || filterSegment === "MovilesPay"
            ? movilesBySede
            : [];
    const filteredEfectivaBySede =
        filterSegment === "ALL" || filterSegment === "Efectiva"
            ? efectivaBySede
            : [];

    // Filtro por marca
    const filteredRankingBrands =
        filterBrand === "ALL"
            ? rankingBrands
            : rankingBrands.filter((r) => r.brand === filterBrand);

    const filteredRankingPhones =
        filterBrand === "ALL"
            ? rankingPhones
            : rankingPhones.filter(
                (r) =>
                    getBrandFromPhone(r.phone).toUpperCase() ===
                    filterBrand.toUpperCase()
            );

    // KPIs avanzados usando listas filtradas
    const bestPhone = filteredRankingPhones[0] || null;
    const bestBrand = filteredRankingBrands[0] || null;
    const bestPayjoySede = payjoyBySede[0] || null;
    const bestPayjoyUser = payjoyByUser[0] || null;

    const formatHour = (h) => `${String(h).padStart(2, "0")}:00`;

    // MODAL user detail

    const [showUserDetailModal, setShowUserDetailModal] = useState(false);
    const [userDetailData, setUserDetailData] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const handleOpenUserDetail = async (user) => {
        try {
            setSelectedUser(user);

            const resp = await axios.get(urlB + "/api/analytics/sales/by-user/detail", {
                params: {
                    startDate: startDate,
                    endDate: endDate,
                    userID: user.userID,
                },
            });

            setUserDetailData(resp.data);
            setShowUserDetailModal(true);

        } catch (err) {
            console.error(err);
        }
    };



    return (
        <div>
            {loading && <PhoneLoader />}
            <ScrollToTop />
            <Header user={user} namePage={props.namePage} />

            <section className="sectionBody sectionBodyAnalytics">
                <div className="containerCustom dashboardVentas">
                    {/* Título página */}
                    <div className="pageHeader">
                        <h1 className="pageTitle">Dashboard de Ventas</h1>
                        <p className="pageSubtitle">
                            Resumen de KPIs, ventas por día, sede, asesor,
                            segmento, FM y comportamiento de compra.
                        </p>
                    </div>

                    {/* Filtros: sede + calendario + botón */}

                    <div
                        style={{
                            display: "flex",
                            justifyContent: 'space-between',
                            alignItems: "center",
                            marginBottom: 20,
                            position: "relative"
                        }}
                    >
                        <div style={{
                            display: "flex",
                            gap: 12,
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}>
                            <div style={{ width: 180 }}>
                                <Select
                                    styles={selectStyles}
                                    options={[
                                        { value: "0", label: "Todas las sedes" },
                                        ...sedes.map((s) => ({
                                            value: s.id,
                                            label: s.name,
                                        })),
                                    ]}
                                    value={{
                                        value: sedeID,
                                        label:
                                            sedeID === "0"
                                                ? "Todas las sedes"
                                                : sedes.find((x) => String(x.id) === String(sedeID))?.name,
                                    }}
                                    onChange={(opt) => setSedeID(opt.value)}
                                />
                            </div>

                            <div style={{ width: 200 }}>
                                <Select
                                    styles={selectStyles}
                                    options={[
                                        { value: "0", label: "Todos los asesores" },
                                        ...advisorOptions.map((u) => ({
                                            value: u.userID,
                                            label: u.fullName,
                                        })),
                                    ]}
                                    value={{
                                        value: filterAsesor,
                                        label:
                                            filterAsesor === "0"
                                                ? "Todos los asesores"
                                                : advisorOptions.find((x) => String(x.userID) === String(filterAsesor))
                                                    ?.fullName,
                                    }}
                                    onChange={(opt) => setFilterAsesor(opt.value)}
                                />
                            </div>

                            <div style={{ width: 180 }}>
                                <Select
                                    styles={selectStyles}
                                    options={[
                                        { value: "ALL", label: "Todos los segmentos" },
                                        { value: "PayJoy", label: "PayJoy" },
                                        { value: "MovilesPay", label: "MovilesPay" },
                                        { value: "Efectiva", label: "Efectiva" },
                                        { value: "Contado", label: "Contado" },
                                        { value: "Otros", label: "Otros" },
                                    ]}
                                    value={{
                                        value: filterSegment,
                                        label:
                                            filterSegment === "ALL"
                                                ? "Todos los segmentos"
                                                : filterSegment,
                                    }}
                                    onChange={(opt) => setFilterSegment(opt.value)}
                                />
                            </div>

                            <div style={{ width: 180 }}>
                                <Select
                                    styles={selectStyles}
                                    options={[
                                        { value: "ALL", label: "Todas las marcas" },
                                        ...brandOptions.map((b) => ({
                                            value: b,
                                            label: b,
                                        })),
                                    ]}
                                    value={{
                                        value: filterBrand,
                                        label: filterBrand === "ALL" ? "Todas las marcas" : filterBrand,
                                    }}
                                    onChange={(opt) => setFilterBrand(opt.value)}
                                />
                            </div>

                            <button
                                onClick={loadData}
                                disabled={loading}
                                className="btn btn-success btn-md"
                            >
                                {loading ? "Actualizando..." : "Actualizar"}
                            </button>
                        </div>
                        {/* ------- */}
                        <div className="filterItem">
                            <div className="calendarFilterWrapper">
                                {showText && (
                                    <div className="calendarFilterMsg">
                                        {showText}
                                    </div>
                                )}
                                <label htmlFor="calendarFilter">
                                    <ImCalendar />
                                </label>
                                <input
                                    readOnly
                                    ref={calendarInputRef}
                                    className="filterDate"
                                    placeholder="Selecciona rango"
                                    id="calendarFilter"
                                />
                            </div>
                        </div>
                    </div>


                    {errorMsg && (
                        <div
                            className="alert alert-danger"
                            style={{ marginTop: "10px" }}
                        >
                            {errorMsg}
                        </div>
                    )}

                    {/* TODOS los KPIs en UN solo bloque */}
                    <div className="kpi-grid">
                        {/* KPIs base */}
                        <div className="kpi-card">
                            <div className="kpi-title">Registros</div>
                            <div className="kpi-value">
                                {formatNumber(kpis.total_registros)}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-title">
                                Ventas Celulares
                            </div>
                            <div className="kpi-value">
                                {formatMoney(
                                    kpis.total_ventas_celulares
                                )}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-title">
                                Total Efectivo
                            </div>
                            <div className="kpi-value">
                                {formatMoney(kpis.total_efectivo)}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-title">
                                Monto Online
                            </div>
                            <div className="kpi-value">
                                {formatMoney(kpis.monto_online)}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-title">
                                Ingresos Globales
                            </div>
                            <div className="kpi-value">
                                {formatMoney(kpis.ingresos_globales)}
                            </div>
                        </div>

                        {/* FM */}
                        <div className="kpi-card">
                            <div className="kpi-title">FM PayJoy</div>
                            <div className="kpi-value">
                                {formatMoney(kpis.fm_payjoy)}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-title">
                                FM MovilesPay
                            </div>
                            <div className="kpi-value">
                                {formatMoney(kpis.fm_movilespay)}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-title">FM Efectiva</div>
                            <div className="kpi-value">
                                {formatMoney(kpis.fm_efectiva)}
                            </div>
                        </div>

                        {/* KPIs derivados */}
                        <div className="kpi-card">
                            <div className="kpi-title">
                                Ticket promedio
                            </div>
                            <div className="kpi-value">
                                {formatMoney(ticketPromedio)}
                            </div>
                            <div className="kpi-helper">
                                Ingreso promedio por registro.
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-title">% Online</div>
                            <div className="kpi-value">
                                {formatPercent(porcentajeOnline)}
                            </div>
                            <div className="kpi-helper">
                                Sobre ingresos globales.
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-title">% Efectivo</div>
                            <div className="kpi-value">
                                {formatPercent(porcentajeEfectivo)}
                            </div>
                            <div className="kpi-helper">
                                Sobre ingresos globales.
                            </div>
                        </div>

                        {/* KPIs avanzados */}
                        {peakHour && (
                            <div className="kpi-card">
                                <div className="kpi-title">
                                    Hora pico de venta
                                </div>
                                <div className="kpi-value">
                                    {formatHour(peakHour.hour)}
                                </div>
                                <div className="kpi-helper">
                                    {formatNumber(
                                        peakHour.totalCount
                                    )}{" "}
                                    ventas en el rango.
                                </div>
                            </div>
                        )}

                        {bestPhone && (
                            <div className="kpi-card">
                                <div className="kpi-title">
                                    Equipo más vendido
                                </div>
                                <div className="kpi-value">
                                    {bestPhone.phone}
                                </div>
                                <div className="kpi-helper">
                                    {formatNumber(bestPhone.units)}{" "}
                                    unidades.
                                </div>
                            </div>
                        )}

                        {bestBrand && (
                            <div className="kpi-card">
                                <div className="kpi-title">
                                    Marca más vendida
                                </div>
                                <div className="kpi-value">
                                    {bestBrand.brand}
                                </div>
                                <div className="kpi-helper">
                                    {formatNumber(bestBrand.units)}{" "}
                                    equipos.
                                </div>
                            </div>
                        )}

                        {bestPayjoySede && (
                            <div className="kpi-card">
                                <div className="kpi-title">
                                    Top PayJoy (sede)
                                </div>
                                <div className="kpi-value">
                                    {bestPayjoySede.sedeName}
                                </div>
                                <div className="kpi-helper">
                                    {formatMoney(
                                        bestPayjoySede.totalPhones
                                    )}{" "}
                                    en equipos.
                                </div>
                            </div>
                        )}

                        {bestPayjoyUser && (
                            <div className="kpi-card">
                                <div className="kpi-title">
                                    Top PayJoy (asesor)
                                </div>
                                <div className="kpi-value">
                                    {bestPayjoyUser.fullName}
                                </div>
                                <div className="kpi-helper">
                                    {formatMoney(
                                        bestPayjoyUser.totalPhones
                                    )}{" "}
                                    en equipos.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Gráfico: ventas por día (estilo referencia, área azul) */}
                    <div className="block mt-5">
                        <div className="blockHeader">
                            <h2>Historial diario</h2>
                            <span className="blockSubtitle">
                                Efectivo + online por fecha.
                            </span>
                        </div>
                        <div className="blockBody chartWrapper">
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart
                                    data={seriesDay}
                                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                >
                                    <defs>
                                        <linearGradient
                                            id="primaryGradient"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor={PRIMARY_COLOR}
                                                stopOpacity={0.35}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor={PRIMARY_COLOR}
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                    </defs>

                                    <CartesianGrid vertical={false} stroke="#F1F5F9" />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                                        axisLine={{ stroke: "#E5E7EB" }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                                        axisLine={{ stroke: "#E5E7EB" }}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        formatter={(value) => formatMoney(value)}
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: "1px solid #E5E7EB",
                                            boxShadow:
                                                "0 8px 20px rgba(15,23,42,0.08)",
                                            fontSize: 11,
                                        }}
                                    />
                                    <Legend />

                                    <Area
                                        type="monotone"
                                        dataKey="totalNet"
                                        name="Total neto"
                                        stroke={PRIMARY_COLOR}
                                        strokeWidth={2}
                                        fill="url(#primaryGradient)"
                                        isAnimationActive={true}
                                        animationDuration={800}
                                        animationEasing="ease-out"
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Gráfico: ventas por sede */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>Historial por sede</h2>
                            <span className="blockSubtitle">
                                Top sedes por monto neto.
                            </span>
                        </div>
                        <div className="blockBody chartWrapper">
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart
                                    data={seriesSede}
                                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                >
                                    <CartesianGrid vertical={false} stroke="#F1F5F9" />
                                    <XAxis
                                        dataKey="sedeNombre"
                                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                                        axisLine={{ stroke: "#E5E7EB" }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                                        axisLine={{ stroke: "#E5E7EB" }}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        formatter={(value) => formatMoney(value)}
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: "1px solid #E5E7EB",
                                            boxShadow:
                                                "0 8px 20px rgba(15,23,42,0.08)",
                                            fontSize: 11,
                                        }}
                                    />
                                    <Legend />

                                    <Bar
                                        dataKey="totalNet"
                                        name="Total neto"
                                        fill={PRIMARY_COLOR}
                                        barSize={18}
                                        radius={[8, 8, 0, 0]}
                                        isAnimationActive={true}
                                        animationDuration={800}
                                        animationEasing="ease-out"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Gráfico: ventas por hora */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>Historial por hora</h2>
                            <span className="blockSubtitle">
                                Comportamiento horario de las ventas de
                                equipos.
                            </span>
                        </div>
                        <div className="blockBody chartWrapper">
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart
                                    data={salesByHour}
                                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                >
                                    <CartesianGrid vertical={false} stroke="#F1F5F9" />
                                    <XAxis
                                        dataKey="hour"
                                        tickFormatter={formatHour}
                                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                                        axisLine={{ stroke: "#E5E7EB" }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                                        axisLine={{ stroke: "#E5E7EB" }}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        formatter={(value, name) =>
                                            name === "totalCount"
                                                ? [
                                                    formatNumber(value),
                                                    "Cantidad",
                                                ]
                                                : [
                                                    formatMoney(value),
                                                    "Monto neto",
                                                ]
                                        }
                                        labelFormatter={(h) =>
                                            `Hora ${formatHour(h)}`
                                        }
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: "1px solid #E5E7EB",
                                            boxShadow:
                                                "0 8px 20px rgba(15,23,42,0.08)",
                                            fontSize: 11,
                                        }}
                                    />
                                    <Legend />

                                    <Bar
                                        dataKey="totalCount"
                                        name="Cantidad"
                                        fill={PRIMARY_COLOR}
                                        barSize={16}
                                        radius={[8, 8, 0, 0]}
                                        isAnimationActive={true}
                                        animationDuration={800}
                                        animationEasing="ease-out"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Tabla: ventas por asesor */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>Ventas por asesor</h2>
                            <span className="blockSubtitle">
                                Ranking por monto de equipos vendidos.
                            </span>
                        </div>
                        <div className="blockBody">
                            <div className="tableResponsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Asesor</th>
                                            <th>Registros</th>
                                            <th>Equipos (S/.)</th>
                                            <th>Efectivo</th>
                                            <th>Online</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSeriesUser.map((u) => (
                                            <tr
                                                key={u.userID}
                                                className="cursor-pointer hover:bg-gray-50"
                                                onClick={() => handleOpenUserDetail(u)}
                                            >
                                                <td>
                                                    {u.fullName ||
                                                        `${u.name} ${u.paternalSur}`}
                                                </td>
                                                <td>
                                                    {formatNumber(
                                                        u.totalRegistros
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        u.totalPhones
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        u.totalCash
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        u.totalOnline
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredSeriesUser.length ===
                                            0 && (
                                                <tr>
                                                    <td
                                                        colSpan={5}
                                                        style={{
                                                            textAlign:
                                                                "center",
                                                        }}
                                                    >
                                                        Sin datos para el
                                                        rango seleccionado.
                                                    </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Tabla: ventas por segmento */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>Ventas por segmento</h2>
                            <span className="blockSubtitle">
                                PayJoy, MovilesPay, Efectiva, Contado,
                                etc.
                            </span>
                        </div>
                        <div className="blockBody">
                            <div className="tableResponsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Segmento</th>
                                            <th>Registros</th>
                                            <th>Equipos (S/.)</th>
                                            <th>Efectivo</th>
                                            <th>Online</th>
                                            <th>FM</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSeriesSegment.map((s) => (
                                            <tr key={s.segmento}>
                                                <td>{s.segmento}</td>
                                                <td>
                                                    {formatNumber(
                                                        s.totalRegistros
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        s.totalPhones
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        s.totalCash
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        s.totalOnline
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        s.totalFM
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredSeriesSegment.length ===
                                            0 && (
                                                <tr>
                                                    <td
                                                        colSpan={6}
                                                        style={{
                                                            textAlign:
                                                                "center",
                                                        }}
                                                    >
                                                        Sin datos para el
                                                        rango seleccionado.
                                                    </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Ranking de PayJoy / MovilesPay / Efectiva por sede */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>
                                Top sedes por segmento (PayJoy,
                                MovilesPay, Efectiva)
                            </h2>
                            <span className="blockSubtitle">
                                Ordenado por monto de equipos.
                            </span>
                        </div>
                        <div className="blockBody">
                            <div className="tableResponsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Segmento</th>
                                            <th>Sede</th>
                                            <th>Equipos (S/.)</th>
                                            <th>Registros</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPayjoyBySede.map((r) => (
                                            <tr
                                                key={
                                                    "py-" + r.sedeID
                                                }
                                            >
                                                <td>{r.segment}</td>
                                                <td>{r.sedeName}</td>
                                                <td>
                                                    {formatMoney(
                                                        r.totalPhones
                                                    )}
                                                </td>
                                                <td>
                                                    {formatNumber(
                                                        r.totalRegistros
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredMovilesBySede.map(
                                            (r) => (
                                                <tr
                                                    key={
                                                        "mv-" +
                                                        r.sedeID
                                                    }
                                                >
                                                    <td>{r.segment}</td>
                                                    <td>
                                                        {r.sedeName}
                                                    </td>
                                                    <td>
                                                        {formatMoney(
                                                            r.totalPhones
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatNumber(
                                                            r.totalRegistros
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                        {filteredEfectivaBySede.map(
                                            (r) => (
                                                <tr
                                                    key={
                                                        "ef-" +
                                                        r.sedeID
                                                    }
                                                >
                                                    <td>{r.segment}</td>
                                                    <td>
                                                        {r.sedeName}
                                                    </td>
                                                    <td>
                                                        {formatMoney(
                                                            r.totalPhones
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatNumber(
                                                            r.totalRegistros
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                        {filteredPayjoyBySede.length ===
                                            0 &&
                                            filteredMovilesBySede.length ===
                                            0 &&
                                            filteredEfectivaBySede.length ===
                                            0 && (
                                                <tr>
                                                    <td
                                                        colSpan={4}
                                                        style={{
                                                            textAlign:
                                                                "center",
                                                        }}
                                                    >
                                                        Sin datos para
                                                        el rango
                                                        seleccionado.
                                                    </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Ranking de FM por asesor y sede */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>FM por asesor y sede</h2>
                            <span className="blockSubtitle">
                                Quién brinda más FM y en qué sedes.
                            </span>
                        </div>
                        <div className="blockBody">
                            <div className="tableResponsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Asesor</th>
                                            <th>FM (conteo)</th>
                                            <th>FM (S/.)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredFmByUser.map((r, i) => (
                                            <tr
                                                key={
                                                    "fmu-" +
                                                    r.userID + i
                                                }
                                            >
                                                <td>{r.fullName}</td>
                                                <td>
                                                    {formatNumber(
                                                        r.totalFMCount
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        r.totalFMAmount
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredFmByUser.length ===
                                            0 && (
                                                <tr>
                                                    <td
                                                        colSpan={3}
                                                        style={{
                                                            textAlign:
                                                                "center",
                                                        }}
                                                    >
                                                        Sin datos para el
                                                        rango seleccionado.
                                                    </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </table>
                            </div>

                            <div
                                className="tableResponsive"
                                style={{ marginTop: "16px" }}
                            >
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Sede</th>
                                            <th>FM (conteo)</th>
                                            <th>FM (S/.)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fmBySede.map((r) => (
                                            <tr
                                                key={
                                                    "fms-" +
                                                    r.sedeID
                                                }
                                            >
                                                <td>{r.sedeName}</td>
                                                <td>
                                                    {formatNumber(
                                                        r.totalFMCount
                                                    )}
                                                </td>
                                                <td>
                                                    {formatMoney(
                                                        r.totalFMAmount
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {fmBySede.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan={3}
                                                    style={{
                                                        textAlign:
                                                            "center",
                                                    }}
                                                >
                                                    Sin datos para el
                                                    rango seleccionado.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Ranking de equipos */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>Ranking de equipos</h2>
                            <span className="blockSubtitle">
                                Modelos más vendidos en el rango.
                            </span>
                        </div>
                        <div className="blockBody">
                            <div className="tableResponsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Equipo</th>
                                            <th>Unidades</th>
                                            <th>Equipos (S/.)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRankingPhones.map(
                                            (r, idx) => (
                                                <tr
                                                    key={
                                                        r.phone + idx
                                                    }
                                                >
                                                    <td>{r.phone}</td>
                                                    <td>
                                                        {formatNumber(
                                                            r.units
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatMoney(
                                                            r.totalPhones
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                        {filteredRankingPhones.length ===
                                            0 && (
                                                <tr>
                                                    <td
                                                        colSpan={3}
                                                        style={{
                                                            textAlign:
                                                                "center",
                                                        }}
                                                    >
                                                        Sin datos para el
                                                        rango seleccionado.
                                                    </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Ranking de marcas */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>Ranking de marcas</h2>
                            <span className="blockSubtitle">
                                La marca es la primera palabra del
                                modelo (Samsung, Xiaomi, etc.).
                            </span>
                        </div>
                        <div className="blockBody">
                            <div className="tableResponsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Marca</th>
                                            <th>Unidades</th>
                                            <th>Equipos (S/.)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRankingBrands.map(
                                            (r, idx) => (
                                                <tr
                                                    key={
                                                        r.brand + idx
                                                    }
                                                >
                                                    <td>{r.brand}</td>
                                                    <td>
                                                        {formatNumber(
                                                            r.units
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatMoney(
                                                            r.totalPhones
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                        {filteredRankingBrands.length ===
                                            0 && (
                                                <tr>
                                                    <td
                                                        colSpan={3}
                                                        style={{
                                                            textAlign:
                                                                "center",
                                                        }}
                                                    >
                                                        Sin datos para el
                                                        rango seleccionado.
                                                    </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Ventas al contado (detalle con filtro por asesor) */}
                    <div className="block">
                        <div className="blockHeader">
                            <h2>Ventas al contado (detalle)</h2>
                            <span className="blockSubtitle">
                                Listado por asesor y sede, filtrable por
                                asesor (filtro global superior).
                            </span>
                        </div>
                        <div className="blockBody">
                            <div className="tableResponsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Fecha / Hora</th>
                                            <th>Sede</th>
                                            <th>Asesor</th>
                                            <th>Equipo</th>
                                            <th>Precio (S/.)</th>
                                            <th>Efectivo</th>
                                            <th>Online</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredContadoList.map((r) => {
                                            const fecha = moment(
                                                r.creationDate
                                            )
                                                .tz(TZ)
                                                .format(
                                                    "DD/MM/YYYY HH:mm"
                                                );
                                            return (
                                                <tr key={r.id}>
                                                    <td>{fecha}</td>
                                                    <td>
                                                        {r.sedeName}
                                                    </td>
                                                    <td>
                                                        {r.fullName}
                                                    </td>
                                                    <td>{r.phone}</td>
                                                    <td>
                                                        {formatMoney(
                                                            r.phonePrice
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatMoney(
                                                            r.cashAmount
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatMoney(
                                                            r.onlineAmount
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filteredContadoList.length ===
                                            0 && (
                                                <tr>
                                                    <td
                                                        colSpan={7}
                                                        style={{
                                                            textAlign:
                                                                "center",
                                                        }}
                                                    >
                                                        Sin ventas al
                                                        contado para el
                                                        filtro seleccionado.
                                                    </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </section >

            <Footer />

            <UserDetailModal
                show={showUserDetailModal}
                onClose={() => setShowUserDetailModal(false)}
                asesor={selectedUser}
                data={userDetailData}
            />

        </div >
    );
}
